import { useLayoutEffect, useRef, useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import type { Model } from '@axilon-apps/domains-identity/types'

import { Button } from '../../components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '../../components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover'
import { cn } from '../../lib/utils'

type UserComboboxProps = {
  id: string
  users: Model.User[]
  value?: string
  onValueChange: (value: string | undefined) => void
  placeholder: string
  includeAll?: boolean
}

export function UserCombobox({
  id,
  users,
  value,
  onValueChange,
  placeholder,
  includeAll = false
}: UserComboboxProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const listContainerRef = useRef<HTMLDivElement>(null)
  const selectedUser = users.find((user) => user.id === value)
  const label = selectedUser?.username ?? (includeAll && !value ? 'All users' : placeholder)

  useLayoutEffect(() => {
    const list = listContainerRef.current?.querySelector<HTMLElement>('[data-slot=command-list]')
    if (list) list.scrollTop = 0
  }, [search])

  const select = (nextValue: string | undefined) => {
    onValueChange(nextValue)
    setSearch('')
    setOpen(false)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen) setSearch('')
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', !selectedUser && !includeAll && 'text-muted-foreground')}>
            {label}
          </span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command
          filter={(itemValue, searchValue, keywords) => {
            const query = searchValue.trim().toLocaleLowerCase()
            if (!query) return 1

            return [itemValue, ...(keywords ?? [])].some((field) =>
              field.toLocaleLowerCase().includes(query)
            )
              ? 1
              : 0
          }}
        >
          <CommandInput value={search} onValueChange={setSearch} placeholder="Search users…" />
          <div ref={listContainerRef}>
            <CommandList>
              <CommandEmpty>No users found.</CommandEmpty>
              <CommandGroup>
                {includeAll ? (
                  <CommandItem value="all users" onSelect={() => select(undefined)}>
                    <Check className={cn('size-4', value ? 'opacity-0' : 'opacity-100')} />
                    All users
                  </CommandItem>
                ) : null}
                {users.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={user.username}
                    keywords={[user.name, user.email ?? '']}
                    onSelect={() => select(user.id)}
                  >
                    <Check
                      className={cn('size-4', value === user.id ? 'opacity-100' : 'opacity-0')}
                    />
                    <span className="min-w-0">
                      <span className="block truncate">{user.username}</span>
                      {user.name ? (
                        <span className="block truncate text-xs text-muted-foreground">
                          {user.name}
                        </span>
                      ) : null}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
